import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, ExternalLink, MapPin, GripVertical } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  adminGetAreas, adminCreateArea, adminUpdateArea, adminDeleteArea, adminReorder,
} from '../api/api';

const VIDE = { name: '', postal_code: '', intro: '', content: '', is_visible: true };

export const AdminAreas = () => {
  const [zones, setZones] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(null);
  const [formulaire, setFormulaire] = useState(VIDE);
  const [glisse, setGlisse] = useState(null);

  const charger = () =>
    adminGetAreas()
      .then(setZones)
      .catch(() => toast.error('Chargement des zones impossible.'))
      .finally(() => setChargement(false));

  useEffect(() => {
    charger();
  }, []);

  const ouvrirCreation = () => {
    setEnEdition(null);
    setFormulaire(VIDE);
    setDialogueOuvert(true);
  };

  const ouvrirEdition = (zone) => {
    setEnEdition(zone);
    setFormulaire({
      name: zone.name,
      postal_code: zone.postal_code || '',
      intro: zone.intro || '',
      content: zone.content || '',
      is_visible: zone.is_visible,
    });
    setDialogueOuvert(true);
  };

  const enregistrer = async () => {
    if (formulaire.name.trim().length < 2) {
      toast.error('Le nom de la commune est requis.');
      return;
    }
    try {
      if (enEdition) {
        await adminUpdateArea(enEdition.id, formulaire);
        toast.success('Zone mise à jour.');
      } else {
        await adminCreateArea(formulaire);
        toast.success('Zone ajoutée.');
      }
      setDialogueOuvert(false);
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enregistrement impossible.');
    }
  };

  const supprimer = async (zone) => {
    if (!window.confirm(`Supprimer la zone « ${zone.name} » ? La page publique associée ne sera plus accessible.`)) return;
    try {
      await adminDeleteArea(zone.id);
      toast.success('Zone supprimée.');
      charger();
    } catch {
      toast.error('Suppression impossible.');
    }
  };

  // Glisser-déposer : l'ordre visuel devient l'ordre affiché sur le site.
  const deposer = async (cible) => {
    if (glisse === null || glisse === cible) return;
    const reordonnees = [...zones];
    const [deplacee] = reordonnees.splice(glisse, 1);
    reordonnees.splice(cible, 0, deplacee);
    setZones(reordonnees);
    setGlisse(null);
    try {
      await adminReorder('areas', reordonnees.map((z) => z.id));
    } catch {
      toast.error('Ordre non enregistré.');
      charger();
    }
  };

  if (chargement) return <div className="p-8 text-gray-500">Chargement…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Zones desservies</h1>
          <p className="text-gray-600 mt-1 max-w-2xl">
            Chaque commune dispose de sa propre page. C'est ce qui permet d'apparaître sur
            des recherches comme « entreprise de nettoyage à Seraing ».
          </p>
        </div>
        <Button onClick={ouvrirCreation} className="gap-2">
          <Plus className="w-4 h-4" aria-hidden="true" />
          Ajouter une commune
        </Button>
      </div>

      {zones.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            Aucune zone pour l'instant.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {zones.map((zone, index) => (
            <Card
              key={zone.id}
              draggable
              onDragStart={() => setGlisse(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => deposer(index)}
              className={`transition-shadow ${glisse === index ? 'opacity-50' : 'hover:shadow-md'}`}
            >
              <CardContent className="flex flex-wrap items-center gap-4 py-4">
                <GripVertical
                  className="w-5 h-5 text-gray-300 cursor-grab shrink-0"
                  aria-hidden="true"
                />

                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <MapPin className="w-4 h-4 text-blue-600" aria-hidden="true" />
                    <span className="font-semibold text-gray-900">{zone.name}</span>
                    {zone.postal_code && (
                      <span className="text-sm text-gray-500">{zone.postal_code}</span>
                    )}
                    {!zone.is_visible && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        masquée
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                    /zones/{zone.slug}
                  </p>
                </div>

                <div className="flex items-center gap-1">
                  <a
                    href={`/zones/${zone.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-50"
                    aria-label={`Voir la page ${zone.name}`}
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                  </a>
                  <button
                    type="button"
                    onClick={() => ouvrirEdition(zone)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-50"
                    aria-label={`Modifier ${zone.name}`}
                  >
                    <Pencil className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => supprimer(zone)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50"
                    aria-label={`Supprimer ${zone.name}`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogueOuvert} onOpenChange={setDialogueOuvert}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{enEdition ? 'Modifier la zone' : 'Nouvelle zone'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="zone-nom">Commune *</Label>
                <Input
                  id="zone-nom"
                  value={formulaire.name}
                  onChange={(e) => setFormulaire((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Seraing"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zone-cp">Code postal</Label>
                <Input
                  id="zone-cp"
                  value={formulaire.postal_code}
                  onChange={(e) => setFormulaire((f) => ({ ...f, postal_code: e.target.value }))}
                  placeholder="4100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zone-intro">Phrase d'introduction</Label>
              <Textarea
                id="zone-intro"
                value={formulaire.intro}
                onChange={(e) => setFormulaire((f) => ({ ...f, intro: e.target.value }))}
                rows={2}
                placeholder="Services de nettoyage professionnel à Seraing et alentours."
              />
              <p className="text-xs text-gray-500">
                C'est le texte que Google affiche sous le titre dans ses résultats.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zone-contenu">Contenu de la page</Label>
              <Textarea
                id="zone-contenu"
                value={formulaire.content}
                onChange={(e) => setFormulaire((f) => ({ ...f, content: e.target.value }))}
                rows={10}
                placeholder={'## Nos interventions à Seraing\n\nDécrivez ici les quartiers couverts, les types de clients…'}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Un texte propre à chaque commune est ce qui distingue vos pages aux yeux de
                Google. Le même paragraphe recopié partout n'apporte rien.
              </p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formulaire.is_visible}
                onChange={(e) => setFormulaire((f) => ({ ...f, is_visible: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
              />
              <span className="text-sm text-gray-700">Page visible sur le site</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogueOuvert(false)}>
              Annuler
            </Button>
            <Button onClick={enregistrer}>{enEdition ? 'Enregistrer' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAreas;
